/**
 * Rendering for the Data surface.
 *
 * Deliberately built on the CLI's existing plain-text table idiom
 * (`lib/sql-output.ts`) rather than the colour/table libraries the standalone
 * Data CLI used: one binary should have one output look, and two renderers with
 * different padding, colour and empty-state wording is a difference users see on
 * every command.
 */

import { sanitizeTerminalText } from '../lib/terminal-output.js';

export type OutputFormat = 'json' | 'table';

/** Render a response for the chosen format and print it. */
export function printOutput(data: unknown, format: OutputFormat): void {
  if (format === 'json') {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  if (Array.isArray(data)) {
    printRows(data as Array<Record<string, unknown>>);
    return;
  }
  if (typeof data === 'object' && data !== null) {
    printFields(data as Record<string, unknown>);
    return;
  }
  console.log(formatCell(data));
}

/** One row per array element, columns taken from the union of the rows' keys. */
export function printRows(rows: Array<Record<string, unknown>>): void {
  if (rows.length === 0) {
    console.log('(no results)');
    return;
  }
  // Union rather than `Object.keys(rows[0])`: Data responses are JSON documents
  // whose optional fields may be absent on the first row, and keying off row 0
  // would silently drop every column that row happens not to carry.
  //
  // Keys are sanitized like values. Record and file types carry TENANT-DEFINED
  // field names, so a column header is as attacker-influenced as a cell.
  // Set beside the ordered array: record and file listings carry wide,
  // per-row-varying key sets, and a linear `includes` inside the per-key loop is
  // quadratic in exactly the case the union exists for.
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row ?? {})) {
      if (seen.has(key)) continue;
      seen.add(key);
      columns.push(key);
    }
  }
  const headers = columns.map((c) => sanitizeTerminalText(c));
  const cells = rows.map((row) => columns.map((c) => cellAt(row, c)));
  // Folded rather than `Math.max(...array)`: spreading a list into arguments
  // throws RangeError once it is large enough, and a full list of records
  // reaches that size.
  const widths = columns.map((_, i) =>
    cells.reduce((width, row) => Math.max(width, row[i].length), headers[i].length),
  );

  console.log(headers.map((c, i) => c.padEnd(widths[i])).join('  '));
  console.log(widths.map((w) => '-'.repeat(w)).join('  '));
  for (const row of cells) {
    console.log(row.map((cell, i) => cell.padEnd(widths[i])).join('  '));
  }
}

/** One `key: value` line per field, for a single-object response. */
export function printFields(obj: Record<string, unknown>): void {
  const entries = Object.entries(obj).map(([key, value]) => ({
    key: sanitizeTerminalText(key),
    value,
  }));
  if (entries.length === 0) {
    console.log('(empty)');
    return;
  }
  const width = entries.reduce((w, e) => Math.max(w, e.key.length), 0);
  for (const { key, value } of entries) {
    console.log(`${key.padEnd(width)}  ${formatCell(value)}`);
  }
}

/**
 * Human rendering for a batch response.
 *
 * The success body is `{ batch_id, results: [...] }` — an object, so the generic
 * `printFields` path JSON-stringifies the whole `results` array into a single
 * unreadable cell. This prints one legible row per operation plus a `batch_id`
 * summary line. `--json` is unchanged and still yields the full per-operation
 * payload.
 *
 * A batch is atomic: a failing operation rolls the whole batch back and returns
 * an error envelope, so every entry in `results` is a success — there are no
 * per-operation failure rows to render here.
 */
export function printBatchResult(response: unknown): void {
  const obj = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
  const results = Array.isArray(obj.results) ? (obj.results as Record<string, unknown>[]) : [];
  const batchId = typeof obj.batch_id === 'string' ? obj.batch_id : '(none)';
  console.log(
    `batch_id: ${sanitizeTerminalText(batchId)} · ${results.length} operation${results.length === 1 ? '' : 's'}`,
  );
  if (results.length === 0) return;
  printRows(
    results.map((r, i) => ({
      '#': String(i),
      operation: r?.type ?? '',
      result: summarizeBatchResult(r),
    })),
  );
}

/** One result's summary: the resulting entity's `<label>/<id>`, "deleted", or compact JSON. */
function summarizeBatchResult(r: Record<string, unknown>): string {
  if (!r || typeof r !== 'object') return String(r ?? '');
  if (r.deleted === true) return 'deleted';
  const entity = r.record ?? r.relationship ?? r.record_type;
  if (entity && typeof entity === 'object') {
    const e = entity as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id : undefined;
    const label =
      typeof e.record_type === 'string'
        ? e.record_type // record → its record type
        : typeof e.rel_type === 'string'
          ? e.rel_type // relationship → its type
          : typeof e.name === 'string'
            ? e.name // record type → its name
            : undefined;
    const version = e.version != null ? ` v${String(e.version)}` : '';
    if (id && label) return `${label}/${id}${version}`;
    if (id) return `${id}${version}`;
  }
  // No recognizable shape — compact JSON of THIS single result, never the whole array.
  return JSON.stringify(r);
}

function cellAt(row: Record<string, unknown> | undefined, column: string): string {
  return formatCell(row?.[column]);
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  // Values are tenant-authored (record data, base names, tag strings), so a
  // control sequence in one could rewrite the surrounding terminal output.
  if (typeof value === 'object') return sanitizeTerminalText(JSON.stringify(value));
  return sanitizeTerminalText(String(value));
}
